import {
  replace,
  Shortcode as WordpressShortcode,
  next,
  attrs as parseShortcodeAttrs,
} from '@wordpress/shortcode';
import {
  replaceBalancedShortcodeTag,
  endOfOpenTag,
} from './balancedShortcodeReplace';
import {
  EnrichedData,
  type EnrichmentContext,
  type ShortCode,
  type ShortCodeRegistry,
} from './types';
import type { ConfigProps } from '../configTypes';

type InternalShortcode = {
  key: string;
  shortcode: ShortCode;
  content: string;
  attrs: Record<string, string>;
  index: number;
};

type EnrichableShortcode = InternalShortcode & {
  shortcode: ShortCode & {
    getEnrichedData: NonNullable<ShortCode['getEnrichedData']>;
  };
};

// The library caches every parsed attribute string with no size limit and
// searches that cache linearly, so it is emptied after every parse.
function parseAttrsUncached(attrString: string) {
  const parsed = parseShortcodeAttrs(attrString);
  parseShortcodeAttrs.clear();
  return parsed;
}

export default class ShortcodeProcessor {
  private shortcodesMap: ShortCodeRegistry;
  private preScannedShortcodes: InternalShortcode[] = [];

  constructor(shortcodesMap: ShortCodeRegistry) {
    this.shortcodesMap = shortcodesMap;
  }

  /**
   * Builds HTML attributes string from shortcode attributes
   * @param shortcode - The shortcode to build attributes from
   * @returns A string of HTML attributes
   */
  private buildAttributes(shortcode: WordpressShortcode): string {
    let attrs = Object.keys(shortcode.attrs.named).reduce((acc, attr) => {
      return `${acc} ${attr}="${shortcode.attrs.named[attr]}"`;
    }, '');
    attrs = Object.keys(shortcode.attrs.numeric).reduce((acc, attr) => {
      return `${acc} ${shortcode.attrs.numeric[Number(attr)]}`;
    }, attrs);

    return attrs;
  }

  private renderClosedShortcode(
    tag: string,
    attrs: string,
    inner: string,
  ): string {
    if (tag !== 'hen-ui-text') {
      return `<${tag}${attrs}>${this.replaceShortcodesWithHtml(inner)}</${tag}>`;
    }

    const leadingSpace = /^\s/.test(inner) ? ' ' : '';
    const trailingSpace = /\s$/.test(inner) ? ' ' : '';
    const content = this.replaceShortcodesWithHtml(inner.trim());

    return `<${tag}${attrs}>${leadingSpace}${content}${trailingSpace}</${tag}>`;
  }

  /**
   * Gets all the shortcodes from the provided content based on the shortcodes map
   * @param content - The content to get the shortcodes from
   * @returns An array of shortcodes with the key and the shortcode
   */
  private getShortCodesFromContent(content: string): InternalShortcode[] {
    const shortcodes: InternalShortcode[] = [];
    if (!content) return [];
    const normalizedContent = this.normalizeQuotes(content);
    Object.keys(this.shortcodesMap).forEach((key) => {
      let searchIndex = 0;
      let instanceIndex = 0;
      let wordpressShortcode = next(key, normalizedContent, searchIndex);
      parseShortcodeAttrs.clear();

      while (wordpressShortcode) {
        const namedAttrs = wordpressShortcode.shortcode.attrs?.named ?? {};
        const attrs: Record<string, string> = {};
        for (const [k, v] of Object.entries(namedAttrs)) {
          if (v !== undefined) attrs[k] = v;
        }

        shortcodes.push({
          key: `${key}:${instanceIndex}`,
          shortcode: this.shortcodesMap[key] as ShortCode,
          content: wordpressShortcode.content,
          attrs,
          index: wordpressShortcode.index,
        });

        searchIndex =
          wordpressShortcode.index + wordpressShortcode.content.length;
        instanceIndex++;
        wordpressShortcode = next(key, normalizedContent, searchIndex);
        parseShortcodeAttrs.clear();
      }
    });
    return shortcodes;
  }

  private isEnrichableShortcode(shortcode: ShortCode): boolean {
    return typeof shortcode.getEnrichedData === 'function';
  }

  /**
   * Gets the enriched data for each shortcode in the content
   * @param content - The content to get the enriched data from
   * @param config - Optional site configuration for data fetching
   * @returns An object with the key and the enriched data
   */
  async getEnrichedDataByShortcodes(
    content: string,
    config?: ConfigProps,
  ): Promise<Record<string, EnrichedData>> {
    const shortcodes = this.getShortCodesFromContent(content);
    this.preScannedShortcodes = shortcodes;

    const shortcodesWithEnrichedContent = shortcodes.filter((shortcode) =>
      this.isEnrichableShortcode(shortcode.shortcode),
    ) as EnrichableShortcode[];

    const results = await Promise.all(
      shortcodesWithEnrichedContent.map(
        ({ key, shortcode, content, attrs }) => {
          const context: EnrichmentContext = { attrs, config };
          return shortcode
            .getEnrichedData(content, context)
            .then((enrichedData) => ({
              key,
              enrichedData,
              enrichedDataError: null,
            }))
            .catch((error: Error) => ({
              key,
              enrichedDataError: error,
              enrichedData: null,
            }));
        },
      ),
    );

    const enrichedData = results.reduce(
      (acc, result) => {
        acc[result.key] = {
          data: result.enrichedData,
          error: result.enrichedDataError,
        };
        return acc;
      },
      {} as Record<string, EnrichedData>,
    );

    return enrichedData;
  }

  /**
   * Normalizes curly/smart quotes to straight quotes
   * WordPress and rich text editors often convert straight quotes to curly quotes
   */
  private normalizeQuotes(content: string): string {
    return content
      .replace(/[\u201C\u201D\u201E\u201F\u2033\u2036]/g, '"') // Double curly quotes → straight
      .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'"); // Single curly quotes → straight
  }

  /**
   * Injects data-ek (enrichment key) attributes into shortcode opening tags
   * so the key survives through HTML conversion and can be read during React parsing.
   * Insert positions are found on the original string and joined in one pass.
   */
  private injectEnrichmentKeys(normalizedContent: string): string {
    if (!this.preScannedShortcodes.length) return normalizedContent;

    // Quote-aware `]` so `data-ek` is not injected into `title="a]b"` etc.
    const insertions = this.preScannedShortcodes
      .map((sc) => ({
        key: sc.key,
        at: endOfOpenTag(normalizedContent, sc.index),
      }))
      .filter(({ at }) => at !== -1)
      .sort((a, b) => a.at - b.at);

    const parts: string[] = [];
    let cursor = 0;
    for (const { key, at } of insertions) {
      parts.push(normalizedContent.slice(cursor, at), ` data-ek="${key}"`);
      cursor = at;
    }
    parts.push(normalizedContent.slice(cursor));
    return parts.join('');
  }

  /**
   * Recursively replaces shortcode tags with HTML elements.
   * Does NOT re-inject enrichment keys — that is done once by the public entry point.
   */
  private replaceShortcodesWithHtml(dirty: string): string {
    Object.keys(this.shortcodesMap).forEach((key) => {
      if (!dirty.includes(`[${key}`)) return;
      const registration = this.shortcodesMap[key];
      if (registration?.balanced) {
        dirty = replaceBalancedShortcodeTag(key, dirty, {
          renderClosed: (attrString, inner) => {
            const shortcodeObj = {
              tag: key,
              attrs: parseAttrsUncached(attrString),
              content: inner,
              type: 'closed' as const,
            } as WordpressShortcode;
            const attrs = this.buildAttributes(shortcodeObj);
            return this.renderClosedShortcode(key, attrs, inner);
          },
          renderSelfClosing: (attrString) => {
            const shortcodeObj = {
              tag: key,
              attrs: parseAttrsUncached(attrString),
              type: 'self-closing' as const,
            } as WordpressShortcode;
            const attrs = this.buildAttributes(shortcodeObj);
            return `<${key}${attrs}></${key}>`;
          },
        });
      } else {
        dirty = replace(key, dirty, (shortcode) => {
          parseShortcodeAttrs.clear();
          const attrs = this.buildAttributes(shortcode);
          if ('content' in shortcode) {
            return this.renderClosedShortcode(
              shortcode.tag,
              attrs,
              shortcode.content ?? '',
            );
          }
          return `<${shortcode.tag}${attrs}></${shortcode.tag}>`;
        });
      }
    });

    dirty = dirty.replace(/(\n|\r)/gm, '');
    dirty = dirty.replace(/\s+/g, ' ').trim();
    dirty = dirty.replace(/> </g, '><');

    return dirty;
  }

  /**
   * This takes a string of html mixed with shortcodes and converts them to html
   * @param dirty - The content string with shortcodes
   * @returns The content string with shortcodes converted to HTML
   */
  convertShortCodesToHtml(dirty: string): string {
    if (!dirty) return '';

    try {
      dirty = this.normalizeQuotes(dirty);
      dirty = this.injectEnrichmentKeys(dirty);

      return this.replaceShortcodesWithHtml(dirty);
    } finally {
      parseShortcodeAttrs.clear();
    }
  }
}
